import { useMemo, memo, useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
// Import UI Components
import { AdvancedSearch } from '@geneui/components';
import GlobalSearchActionsModals from './GlobalSearchActionsModals';
// Import Services
import { UtilsHttpService } from 'services/http';
// Import Hooks
import useAutocompleteRequest from 'hooks/UseAutocompleteRequest';
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useHasPermission from 'hooks/UseHasPermission';
import useSearchRedirectionAndModal from 'hooks/UseSearchRedirectionAndModal';
import { useOnClickOutside } from 'hooks';
import { useGTMEvent } from 'hooks/UseGTM';
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
// Import Constants
import { GACategoryEvents, l } from 'constants/common';
import { getObjectTypes, searchActionKeys } from './config';
// Import Enums
import { ObjectTypeIdsEnum } from 'enums';

const { GASearchCategory } = GACategoryEvents;
// keeps the search event tied to a finished keyword instead of every keystroke
const SEARCH_EVENT_DEBOUNCE_TIMEOUT = 700;

const GlobalSearch = () => {
    const { t } = useTranslation();
    const { hasPermission } = useHasPermission();
    const { doGetRequest, doPostRequest } = useRequest();
    const { getAutocompleteRequest, cancelRequest } = useAutocompleteRequest();
    const dateFormats = usePartnerDateFormats();
    const searchContainerRef = useRef<HTMLDivElement>(null);

    const [isOpen, setIsOpen] = useState(false);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [selectedObjectTypes, setSelectedObjectTypes] = useState<AnyObject[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<AnyObject[]>([]);
    const [searchResult, setSearchResult] = useState([]);
    const [isSearchLoading, setIsSearchLoading] = useState(true);
    const { logEvent } = useGTMEvent();

    const {
        modalsStateSetDict,
        openedModalData,
        isSegmentInfoModalOpen,
        isTemplateInfoModalOpen,
        isCampaignInfoModalOpen,
        isRealtimePromotionInfoModalOpen,
        isReportInfoModalOpen,
        addActionsToData,
    } = useSearchRedirectionAndModal();

    const { getUsersRequest } = useCancelableRequests({
        getUsersRequest: UtilsHttpService.getGlobalSearchUsers,
    });

    const onSearch = useCallback((keyword) => {
        setIsOpen(true);
        setSearchKeyword(keyword);
    }, []);

    const onObjectTypeSelect = useCallback(
        (objectTypes) => {
            const checkedObjectTypes = objectTypes.filter((o) => o.checked);

            setSelectedObjectTypes(checkedObjectTypes);
            logEvent(
                GASearchCategory.name,
                GASearchCategory.events.filterByObjectType,
                checkedObjectTypes.map(({ id }) => ObjectTypeIdsEnum[id]).join(', ') ||
                    GASearchCategory.labels.filterByObjectTypeEmpty,
                checkedObjectTypes.length,
            );
        },
        [logEvent],
    );

    const onUserSelect = useCallback(
        (users) => {
            const checkedUsers = users.filter((u) => u.checked);

            setSelectedUsers(checkedUsers);
            // user names are not sent to analytics, only how many are selected
            logEvent(
                GASearchCategory.name,
                GASearchCategory.events.filterByUser,
                GASearchCategory.labels.filterByUser,
                checkedUsers.length,
            );
        },
        [logEvent],
    );

    const [usersFilterSettings, setUsersFilterSettings] = useState({
        sectionNameText: t(l.FilterByUser),
        isLoading: true,
        data: [],
    });

    const usersFilterData = useMemo(
        () => ({ ...usersFilterSettings, onChange: onUserSelect }),
        [usersFilterSettings, onUserSelect],
    );

    const objectTypeFilterSettings = useMemo(() => {
        const allObjectTypes = getObjectTypes(t);
        const allowedObjectTypes = allObjectTypes.filter(
            ({ permissions }) => isEmpty(permissions) || hasPermission(permissions),
        );
        return { sectionNameText: t(l.FilterByObjectType), onChange: onObjectTypeSelect, data: allowedObjectTypes };
    }, [hasPermission, onObjectTypeSelect, t]);

    const openPopover = useCallback(() => {
        setIsOpen(true);
        if (!isOpen) {
            setIsSearchLoading(true);
        }
    }, [isOpen]);

    const closePopover = useCallback(() => {
        setIsOpen(false);
    }, []);

    const addSearchResultEvents = useCallback(
        (rows) =>
            rows.map((row) => ({
                ...row,
                actions: row.actions?.map((action) => ({
                    ...action,
                    onClick: () => {
                        logEvent(
                            GASearchCategory.name,
                            action.actionKey === searchActionKeys.info
                                ? GASearchCategory.events.resultInfo
                                : GASearchCategory.events.resultView,
                            ObjectTypeIdsEnum[row.rowData?.ObjectTypeId],
                        );

                        action.onClick();
                    },
                })),
            })),
        [logEvent],
    );

    const fetchSearchResult = useCallback(() => {
        setIsSearchLoading(true);
        const requestObj = getAutocompleteRequest(UtilsHttpService.getGlobalSearchResult);

        doPostRequest(requestObj.request, {
            requestBody: {
                Name: searchKeyword,
                ObjectTypes: selectedObjectTypes.map((objectType) => objectType.value),
                UserIds: selectedUsers.map((user) => user.value),
            },
            successCallback: (data) => {
                const parsedData = addActionsToData({ data, closePopover, dateFormats });

                setSearchResult(addSearchResultEvents(parsedData));
                setIsSearchLoading(false);
            },
            errorCallback: () => {
                setSearchResult([]);
            },
        });
    }, [
        addActionsToData,
        addSearchResultEvents,
        closePopover,
        dateFormats,
        doPostRequest,
        getAutocompleteRequest,
        searchKeyword,
        selectedObjectTypes,
        selectedUsers,
    ]);

    const onCloseModalHandler = (modalStateKey) => {
        modalsStateSetDict[modalStateKey].fn(false);
    };

    const fetchUsersList = useCallback(() => {
        doGetRequest(getUsersRequest.request, {
            successCallback: (data) => {
                const parsedUsers = data.map((user) => ({
                    id: user.UserId,
                    name: user.Name,
                    value: String(user.UserId),
                    checked: false,
                }));

                setUsersFilterSettings((current) => ({
                    ...current,
                    isLoading: false,
                    data: parsedUsers,
                }));
            },
            errorCallback: () => {
                setUsersFilterSettings((current) => ({
                    ...current,
                    isLoading: false,
                }));
            },
        });
    }, [doGetRequest, getUsersRequest]);

    const extendedInputConfigs = useMemo(
        () => ({
            onFocus: openPopover,
            onIconClick: openPopover,
            type: 'text',
            maxLength: 100,
            placeholder: t(l.Search),
        }),
        [openPopover, t],
    );

    useEffect(() => {
        if (isOpen && isEmpty(usersFilterSettings.data)) {
            fetchUsersList();
        }
    }, [isOpen, fetchUsersList, usersFilterSettings.data]);

    useEffect(() => {
        if (isOpen) {
            fetchSearchResult();
        } else {
            cancelRequest();
        }
    }, [searchKeyword, selectedObjectTypes, selectedUsers, isOpen, fetchSearchResult, cancelRequest]);

    useEffect(() => {
        const keyword = searchKeyword.trim();

        if (!keyword) return;

        const searchEventTimeout = setTimeout(() => {
            logEvent(
                GASearchCategory.name,
                GASearchCategory.events.globalSearch,
                GASearchCategory.labels.globalSearch,
                keyword,
            );
        }, SEARCH_EVENT_DEBOUNCE_TIMEOUT);

        return () => {
            clearTimeout(searchEventTimeout);
        };
    }, [searchKeyword, logEvent]);
    const cleanUp = () => () => {
        cancelRequest();
    };

    useOnClickOutside(searchContainerRef, closePopover, false, true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(cleanUp, []);

    return (
        <div className="global-search-container" ref={searchContainerRef}>
            <AdvancedSearch
                isOpen={isOpen}
                onOutsideClick={closePopover}
                data={searchResult}
                position="left"
                noDataText={t(l.NoDataToDisplay)}
                isSearchLoading={isSearchLoading}
                initialData={[]}
                primaryFilterData={objectTypeFilterSettings}
                secondaryFilterData={usersFilterData}
                initialDataDescription={t(l.Result)}
                onSearch={onSearch}
                totalCount={searchResult.length}
                totalCountMax={19}
                totalCountText={t(l.Result)}
                extendedInputConfigs={extendedInputConfigs}
            />
            <GlobalSearchActionsModals
                isSegmentInfoModalOpen={isSegmentInfoModalOpen}
                isTemplateInfoModalOpen={isTemplateInfoModalOpen}
                isCampaignInfoModalOpen={isCampaignInfoModalOpen}
                isRealtimePromotionInfoModalOpen={isRealtimePromotionInfoModalOpen}
                isReportInfoModalOpen={isReportInfoModalOpen}
                openedModalData={openedModalData}
                infoModalsStateSetDict={modalsStateSetDict}
                onCloseModalHandler={onCloseModalHandler}
            />
        </div>
    );
};

export default memo(GlobalSearch);
